import { Button as HeroButton } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { ProgressCircle } from '@components/ProgressCircle'
import { ScrollBar } from '@components/ScrollBar'
import { useCollapsibleToggle } from '@hooks/useCollapsibleToggle'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { useModules } from '@hooks/useModules'
import { Portal } from '@tamagui/portal'
import { useLocalSearchParams } from 'expo-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AnimatePresence,
  Button,
  isWeb,
  Text,
  View,
  XStack,
  YStack,
} from 'tamagui'
import { useWindowDimensions } from 'react-native'
import { ContentsModules } from './ContentsModules'
import { ContentProgress } from './ContenProgress'
import { NextLearningCard } from './NextLearning/NextLearningCard'

const STICKY_LIST_TOP = 100
const STICKY_LIST_BOTTOM_GAP = 24

/** Fração da tela ocupada pelo painel aberto. */
const EXPANDED_RATIO = 0.8
/** Altura da barra com o painel recolhido. */
const COLLAPSED_HEIGHT = 120

interface IContentsCollapsibleWebProps {
  type: 'bottomFixed' | 'topOpened'
}

export function ContentsCollapsibleWeb({
  type = 'bottomFixed',
}: IContentsCollapsibleWebProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const { t } = useTranslation('CourseDetails')
  const [scrollBarHeight, setScrollBarHeight] = useState(0)

  const { modules, onLoadMore, onLoadMoreSubmodules, hasMoreSubmodules } =
    useModules()
  const { enrollment } = useEnrollment()
  const { course } = useCourse()
  const { moduleId } = useLocalSearchParams()
  const modalChevronIcon = isExpanded ? 'chevron-down' : 'chevron-up'

  const { isCollapsibleOpen, toggleCollapsible } = useCollapsibleToggle()
  const { height: windowHeight } = useWindowDimensions()

  const togglePosition = () => {
    setIsExpanded(!isExpanded)
  }

  const filteredModules = useMemo(() => {
    if (!modules?.nodes) return []
    return modules.nodes.filter((module) => module.available !== false)
  }, [modules])

  const renderModules = useMemo(() => {
    return filteredModules.map((module, index) => {
      const isModuleOpen = module.id.toString() === moduleId
      const isLastModule = index === filteredModules.length - 1
      return (
        <ContentsModules
          key={module.id}
          module={module}
          enrollment={enrollment}
          course={course}
          moduleId={moduleId}
          onLoadMore={onLoadMore}
          // Sem onLoadMoreSubmodules o AccordionModule nunca pede as próximas
          // páginas de submódulos e módulos com mais de 20 ficam truncados.
          onLoadMoreSubmodules={onLoadMoreSubmodules}
          hasMoreSubmodules={hasMoreSubmodules}
          isLastModule={isLastModule}
          isModuleOpen={isModuleOpen}
          isSubmodule={false}
        />
      )
    })
  }, [
    filteredModules,
    moduleId,
    onLoadMore,
    onLoadMoreSubmodules,
    hasMoreSubmodules,
    enrollment,
    course,
  ])

  const iconColor = isActive ? '$accent' : '$text-muted'

  // O container do portal é `position: fixed` com `bottom: 0` e sem altura, e
  // percentual não resolve contra pai de altura automática. Com `height: '80%'`
  // o painel aberto caía em `auto` e crescia com a lista: passava da tela, e a
  // área de scroll (`flex: 1` medida por onLayout) herdava o tamanho do
  // conteúdo, então nunca sobrava o que rolar — e a paginação seguia carregando
  // módulo atrás de módulo, por nunca alcançar o fim da lista. Medido em
  // 768x438: painel de 3814px com o topo em -3260; com a altura em px, 350px
  // dentro da tela e o scroll de volta.
  const expandedHeight = useMemo(() => {
    return Math.round(windowHeight * EXPANDED_RATIO)
  }, [windowHeight])

  // No web a lista acompanha a rolagem da página e ocupa a altura da tela. O
  // `top` é onde ela já começa (cabeçalho fixo + margem do topo da aula), para
  // não pular ao grudar. No app (tablet) continua com a altura fixa de antes.
  const stickyListStyle = isWeb
    ? ({
        position: 'sticky',
        alignSelf: 'flex-start',
        top: STICKY_LIST_TOP,
        height: `calc(100vh - ${STICKY_LIST_TOP + STICKY_LIST_BOTTOM_GAP}px)`,
      } as unknown as object)
    : undefined

  if (type === 'topOpened') {
    return (
      <AnimatePresence>
        {isCollapsibleOpen && (
          <YStack
            key="collapsible-web-top-opened"
            animation="quick"
            enterStyle={{
              opacity: 0,
              x: 100,
            }}
            exitStyle={{
              opacity: 0,
              x: 100,
            }}
            opacity={1}
            x={0}
            minWidth={isCollapsibleOpen ? 407 : 0}
            width={isCollapsibleOpen ? '25%' : '0%'}
            backgroundColor="$bg-surface"
            maxHeight={isWeb ? undefined : 672}
            borderRadius={'$border-radius-md'}
            overflow="hidden"
            marginLeft="auto"
            style={stickyListStyle}
          >
            <YStack padding="$spacing-inset-sm" gap="$spacing-inset-xs">
              <XStack justifyContent="flex-end">
                <HeroButton
                  variant="primary"
                  testID="header-back-button"
                  aria-label="Back"
                  link
                  paddingLeft={0}
                  onPress={toggleCollapsible}
                >
                  <XStack alignItems="center">
                    <Text
                      fontSize="$font-size-xxs"
                      ff="$mulishRegular"
                      color="$accent"
                      position="relative"
                      textDecorationLine="underline"
                    >
                      {t('Esconder Lista')}
                    </Text>
                    <HeroIcon
                      icon={['fal', 'chevron-right']}
                      color="$accent"
                      size={16}
                    />
                  </XStack>
                </HeroButton>
              </XStack>
              <XStack>
                <Text
                  color="$text-primary"
                  fontSize="$font-size-xs"
                  fontFamily="$mulishSemiBold"
                  fontWeight="600"
                  width="90%"
                  numberOfLines={3}
                >
                  {course?.name}
                </Text>
              </XStack>
              <XStack alignItems="center" gap="$spacing-inset-xs">
                <ProgressCircle
                  progress={enrollment?.progressCard?.progress || 0}
                  size={64}
                  strokeWidth={6}
                  progressColorToken="$accent"
                  showPercentageLabel
                />
                <YStack flex={1} gap="$spacing-inset-quarck">
                  <Text
                    color="$text-primary"
                    fontSize="$font-size-xs"
                    fontFamily="$mulishSemiBold"
                    fontWeight="600"
                  >
                    {t('Seu progresso')}
                  </Text>
                  <Text
                    color="$text-primary"
                    fontSize="$font-size-xxs"
                    fontFamily="$mulishRegular"
                  >
                    <Text
                      color="$progress-complete"
                      fontFamily="$mulishBold"
                      fontWeight="700"
                      fontSize="$font-size-xxs"
                    >
                      {enrollment?.progressCard?.completed}
                    </Text>
                    {` ${t('de')} ${enrollment?.progressCard?.total} ${t(
                      'conteúdos',
                    )}`}
                  </Text>
                </YStack>
              </XStack>
            </YStack>
            <YStack
              flex={1}
              overflow="hidden"
              padding="$spacing-inset-sm"
              paddingTop={0}
              onLayout={(event) =>
                setScrollBarHeight(event.nativeEvent.layout.height)
              }
            >
              <ScrollBar
                maxHeight={scrollBarHeight}
                displayNativeIndicator={false}
                scrollBarId="contents-modal-scroll"
              >
                {renderModules}
              </ScrollBar>
            </YStack>
            <NextLearningCard />
            <XStack
              h={56}
              borderTopWidth={1}
              borderColor="$border-default"
              marginTop="auto"
            />
          </YStack>
        )}
      </AnimatePresence>
    )
  }

  return (
    <Portal>
      <View
        tag="div"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 99999999999,
          width: '100%',
        }}
        pointerEvents="box-none"
      >
        <YStack
          width="100%"
          height={isExpanded ? expandedHeight : COLLAPSED_HEIGHT}
          backgroundColor="transparent"
          pointerEvents="auto"
          testID="contents-collapsible-panel"
        >
          <AnimatePresence>
            <YStack
              key="collapsible-content"
              animation="quick"
              enterStyle={{
                opacity: 0,
                y: 100,
              }}
              exitStyle={{
                opacity: 0,
                y: 100,
              }}
              opacity={1}
              y={0}
              backgroundColor="$bg-surface"
              borderTopLeftRadius="$border-radius-xlg"
              borderTopRightRadius="$border-radius-xlg"
              height={isExpanded ? '100%' : COLLAPSED_HEIGHT}
              pointerEvents="auto"
              overflow="hidden"
            >
              <XStack
                p="$spacing-inset-xs"
                gap="$spacing-inset-nano"
                alignItems="center"
                height={100}
              >
                <ProgressCircle
                  progress={enrollment?.progressCard?.progress || 0}
                />
                <YStack flex={1} gap="$spacing-inset-quarck">
                  <Text
                    color="$text-primary"
                    fontSize="$font-size-xs"
                    fontFamily="$mulishSemiBold"
                    fontWeight="600"
                    width="90%"
                    numberOfLines={3}
                  >
                    {course?.name}
                  </Text>
                  {enrollment && <ContentProgress enrollment={enrollment} />}
                </YStack>
                <Button
                  testID="contents-collapsible-toggle"
                  onPress={togglePosition}
                  backgroundColor="transparent"
                  size={32}
                  onHoverIn={() => setIsActive(true)}
                  onHoverOut={() => setIsActive(false)}
                  onPressIn={() => setIsActive(true)}
                  onPressOut={() => setIsActive(false)}
                  hoverStyle={{
                    backgroundColor: '$bg-surface-pressed',
                    outlineWidth: 0,
                  }}
                  pressStyle={{
                    scale: 0.97,
                    backgroundColor: '$bg-surface-pressed',
                    outlineWidth: 0,
                  }}
                  focusStyle={{ outlineWidth: 0 }}
                >
                  <HeroIcon
                    icon={['fal', modalChevronIcon]}
                    size={16}
                    color={iconColor}
                  />
                </Button>
              </XStack>
              {!isExpanded && (
                <YStack
                  key="separator"
                  backgroundColor="$bg-surface"
                  borderColor="$border-default"
                  borderWidth={1}
                  marginHorizontal="$spacing-inset-xs"
                />
              )}

              <YStack
                overflow="hidden"
                flex={1}
                padding="$spacing-inset-xs"
                onLayout={(event) =>
                  setScrollBarHeight(event.nativeEvent.layout.height)
                }
              >
                <ScrollBar
                  maxHeight={scrollBarHeight}
                  displayNativeIndicator={false}
                  scrollBarId="contents-modal-scroll"
                >
                  {renderModules}
                </ScrollBar>
              </YStack>
              {isExpanded && <NextLearningCard />}
            </YStack>
          </AnimatePresence>
        </YStack>
      </View>
    </Portal>
  )
}
